import { useState, useRef, useEffect } from "react";
import { Document, Page } from "react-pdf";
import Reveal from "./Reveal";


const PdfViewer = ({
  pdfUrl,
  zoom = 100,
  isFullscreen,
}: {
  pdfUrl: string;
  zoom?: number;
  isFullscreen?: boolean;
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const pdfWrapperRef = useRef<HTMLDivElement>(null);

  // 1. Handle Document Load Success
  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1); // Reset to first page when new document loads
  }

  // 2. Fit to Width Logic (Resize Observer)
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width - 64); // Subtract padding
      }
    });

    if (pdfWrapperRef.current) {
      resizeObserver.observe(pdfWrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  };

  const goToFirstPage = () => {
    setPageNumber(1);
  };

  const goToLastPage = () => {
    setPageNumber(numPages || 1);
  };

  // Calculate page width based on zoom
  const pageWidth = containerWidth ? (containerWidth * zoom) / 100 : undefined;

  return (
    <Reveal delay={120}>
      <div
        id="pdf-container"
        className="relative overflow-hidden rounded-3xl border border-white/50 bg-white/90 shadow-[0_20px_50px_-30px_rgba(9,32,63,0.3)]"
      >
        {/* Page Navigation Controls */}
        {numPages && numPages > 1 && (
          <div className="flex items-center justify-between border-b border-accentNavy/10 bg-white/50 px-6 py-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={goToFirstPage}
                disabled={pageNumber === 1}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="First page"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToPrevPage}
                disabled={pageNumber === 1}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>

            <div className="text-center">
              <span className="text-sm font-semibold text-accentNavy">
                Page {pageNumber} of {numPages}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={goToNextPage}
                disabled={pageNumber === numPages}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToLastPage}
                disabled={pageNumber === numPages}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-accentNavy/20 bg-white text-accentNavy transition-all duration-200 hover:border-accentGold hover:bg-accentGold hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Last page"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div
          ref={pdfWrapperRef}
          className="relative w-full overflow-auto bg-gray-100"
          style={{
            height: isFullscreen ? "100%" : "80vh",
            minHeight: "600px",
          }}
        >
          <div className="mx-auto flex flex-col items-center py-8">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="p-10">Loading PDF...</div>}
              error={<div className="p-10 text-red-500">Failed to load PDF.</div>}
            >
              {numPages && (
                <div className="shadow-lg">
                  <Page
                    pageNumber={pageNumber}
                    width={pageWidth ? Math.min(pageWidth, 1200) : undefined}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              )}
            </Document>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export default PdfViewer;